import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  CircularProgress,
  DialogTitle,
  Theme,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import arrayMutators from "final-form-arrays";
import { pick } from "lodash";
import { useCallback, useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { FORM_ERROR } from "final-form";
import {
  IQualification,
  QualificationStatement,
  QualificationStatementRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import useGlobalContext from "hooks/globalData";
import SelectQualificationStatementType from "./SelectQualificationStatementType";
import ContentSearch from "components/ContentSearch";
import { Mode } from "types/common-types";
import SelectProfession from "components/SelectProfession";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const { Form } = withTypes<QualificationStatementRequest>();

type QualificationStatementFormProps = {
  qualification: IQualification;
  qualificationStatement?: QualificationStatement | null;
  title: string;
  onCancel?: () => void;
  onSubmit: () => void;
};

const QualificationStatementForm = ({
  qualification,
  qualificationStatement,
  title,
  onCancel,
  onSubmit,
}: QualificationStatementFormProps &
  FormProps<QualificationStatementRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { qualificationService } = useGlobalContext();

  const init = useMemo(
    () => ({
      qualificationId: qualification.id,
      professionId: qualificationStatement?.profession?.id,
      ...pick(
        {
          ...qualificationStatement,
        },
        ["id", "qualificationStatementTypeId", "contentName", "sequence"]
      ),
    }),
    [qualification.id, qualificationStatement]
  );

  const handleSubmit = useCallback(
    async (values: QualificationStatementRequest) => {
      try {
        await qualificationService.saveQualificationStatement(
          qualification.id!,
          {
            ...values,
          } as QualificationStatementRequest
        );

        onSubmit();
      } catch (error) {
        return { [FORM_ERROR]: translateError(error, t) };
      }
    },
    [onSubmit, qualification.id, qualificationService, t]
  );

  return useMemo(() => {
    return (
      <Form
        onSubmit={handleSubmit}
        initialValues={init}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle id="form-dialog-title">{title}</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="qualificationStatementTypeId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectQualificationStatementType
                          required
                          label={t({
                            id: "qualificationStatementFormTypeLabel",
                          })}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="contentName" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <ContentSearch
                          {...input}
                          required
                          mode={Mode.Update}
                          label={t({
                            id: "qualificationStatementFormContentNameLabel",
                          })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="professionId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectProfession
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          label={t({ id: "professionLabel" })}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              {submitting && <CircularProgress size={24} />}
              <Button
                disabled={submitting}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="saveButton"
              >
                {t({ id: "qualificationStatementFormSaveButton" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    handleSubmit,
    init,
    title,
    styles.feedback,
    onCancel,
    t,
  ]);
};

export default QualificationStatementForm;
